import { blurImageData, type ImageDataLike } from "@kayahr/stackblur";

export type FaceRegion = { x: number; y: number; width: number; height: number };

// A radius relative to the box size removes facial features at any distance
// from the camera. Stackblur clamps to 254.
const MIN_BLUR_RADIUS = 16;
const MAX_BLUR_RADIUS = 254;
const BLUR_RADIUS_RATIO = 0.35;

// Outline drawn around each detected face box, on top of the blur.
const BOX_STROKE_COLOR = "#00e676";
const BOX_STROKE_RATIO = 0.004;
const MIN_BOX_STROKE_WIDTH = 2;

/** Blurs every detected face box, in place. */
export function blurFaceRegions(
  image: ImageDataLike,
  boxes: FaceRegion[],
): void {
  for (const box of boxes) {
    const region = clampRegion(box, image.width, image.height);
    if (region.width <= 0 || region.height <= 0) continue;
    blurRegion(image, region);
  }
}

/**
 * Decodes the JPEG, blurs faces and encodes it again. Runs in a worker when
 * OffscreenCanvas is available, otherwise on the main thread.
 */
export async function anonymizeImage(
  image: Blob,
  boxes: FaceRegion[],
  frame: { width: number; height: number },
): Promise<Blob> {
  const bitmap = await createImageBitmap(image);
  try {
    const { width, height } = bitmap;
    const canvas = createCanvas(width, height);
    const context = canvas.getContext("2d") as
      | CanvasRenderingContext2D
      | OffscreenCanvasRenderingContext2D
      | null;
    if (!context) throw new Error("Canvas 2D context is unavailable.");

    context.drawImage(bitmap, 0, 0);
    const imageData = context.getImageData(0, 0, width, height);
    const scaleX = frame.width ? width / frame.width : 1;
    const scaleY = frame.height ? height / frame.height : 1;
    const scaledBoxes = boxes.map((box) => ({
      x: box.x * scaleX,
      y: box.y * scaleY,
      width: box.width * scaleX,
      height: box.height * scaleY,
    }));
    blurFaceRegions(imageData, scaledBoxes);
    context.putImageData(imageData, 0, 0);

    context.strokeStyle = BOX_STROKE_COLOR;
    context.lineWidth = Math.max(
      MIN_BOX_STROKE_WIDTH,
      Math.round(Math.max(width, height) * BOX_STROKE_RATIO),
    );
    for (const box of scaledBoxes) {
      context.strokeRect(box.x, box.y, box.width, box.height);
    }

    return await encodeJpeg(canvas);
  } finally {
    bitmap.close();
  }
}

function clampRegion(
  box: FaceRegion,
  frameWidth: number,
  frameHeight: number,
): FaceRegion {
  const left = Math.max(0, Math.floor(box.x));
  const top = Math.max(0, Math.floor(box.y));
  const right = Math.min(frameWidth, Math.ceil(box.x + box.width));
  const bottom = Math.min(frameHeight, Math.ceil(box.y + box.height));
  return { x: left, y: top, width: right - left, height: bottom - top };
}

function blurRegion(image: ImageDataLike, region: FaceRegion): void {
  const { x, y, width, height } = region;
  const rowLength = width * 4;
  const pixels = new Uint8ClampedArray(rowLength * height);

  for (let row = 0; row < height; row += 1) {
    const start = ((y + row) * image.width + x) * 4;
    pixels.set(image.data.subarray(start, start + rowLength), row * rowLength);
  }

  const radius = Math.min(
    MAX_BLUR_RADIUS,
    Math.max(MIN_BLUR_RADIUS, Math.round(Math.max(width, height) * BLUR_RADIUS_RATIO)),
  );
  blurImageData({ width, height, data: pixels }, radius, false);

  for (let row = 0; row < height; row += 1) {
    const start = ((y + row) * image.width + x) * 4;
    image.data.set(pixels.subarray(row * rowLength, (row + 1) * rowLength), start);
  }
}

function createCanvas(
  width: number,
  height: number,
): HTMLCanvasElement | OffscreenCanvas {
  if (typeof OffscreenCanvas !== "undefined") {
    return new OffscreenCanvas(width, height);
  }
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

function encodeJpeg(canvas: HTMLCanvasElement | OffscreenCanvas): Promise<Blob> {
  if ("convertToBlob" in canvas) {
    return canvas.convertToBlob({ type: "image/jpeg", quality: 0.9 });
  }
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("JPEG encoding failed."))),
      "image/jpeg",
      0.9,
    );
  });
}
